import errorDatabase from 'src/assets/data/whatsapp-meta-error-codes.json';

export interface WhatsappErrorCodeMeta {
  code: number;
  title: string;
  short_name: string;
  category: string;
  retriable: boolean;
  severity: 'danger' | 'warning' | 'info' | 'secondary';
  icon: string;
  description: string;
  action: string;
}

export interface WhatsappCategoryMeta {
  key: string;
  label: string;
  icon: string;
  color: string;
  retriable: boolean;
}

export interface ExtractedWhatsappError {
  code: number | null;
  title: string;
  shortName: string;
  message: string;
  details: string;
  action: string;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  retriable: boolean;
  icon: string;
  severity: 'danger' | 'warning' | 'info' | 'secondary';
  badgeText: string;
  rawError: any;
}

const ERROR_DB: Record<string, WhatsappErrorCodeMeta> = (errorDatabase as any)?.errors || {};
const CATEGORY_DB: Record<string, WhatsappCategoryMeta> = (errorDatabase as any)?.categories || {};

/**
 * Safely parse JSON string, handles double-stringified JSON payloads.
 */
export function safeJsonParse(data: any): any {
  if (!data) return null;
  if (typeof data === 'object') return data;
  if (typeof data !== 'string') return null;

  try {
    let parsed = JSON.parse(data);
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (_) {
        // use single-parsed
      }
    }
    return parsed;
  } catch (err) {
    return null;
  }
}

/**
 * Extract WhatsApp error information from raw_event_data or tracking payload.
 */
export function extractWhatsappError(rawEventData: any): ExtractedWhatsappError | null {
  const parsed = safeJsonParse(rawEventData);
  if (!parsed) return null;

  let rawErr: any = null;

  // 1. Meta Cloud API standard structure: entry[0].changes[0].value.statuses[0].errors[0]
  const metaStatuses = parsed?.entry?.[0]?.changes?.[0]?.value?.statuses;
  if (Array.isArray(metaStatuses)) {
    for (const statusObj of metaStatuses) {
      if (Array.isArray(statusObj?.errors) && statusObj.errors.length > 0) {
        rawErr = statusObj.errors[0];
        break;
      }
      if (statusObj?.error) {
        rawErr = statusObj.error;
        break;
      }
    }
  }

  // 2. Direct errors array or error object in payload
  if (!rawErr) {
    if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
      rawErr = parsed.errors[0];
    } else if (parsed?.error) {
      rawErr = parsed.error;
    }
  }

  // 3. Gupshup nested structures: payload.payload.errors, payload.errors, etc.
  if (!rawErr) {
    const layer1 = parsed.payload || parsed;
    const layer2 = layer1.payload || layer1;
    if (Array.isArray(layer2?.errors) && layer2.errors.length > 0) {
      rawErr = layer2.errors[0];
    } else if (layer2?.error) {
      rawErr = layer2.error;
    } else if (layer2?.cause || layer2?.reason) {
      rawErr = {
        code: layer2.code || null,
        message: layer2.cause || layer2.reason,
        title: layer2.reason || layer2.cause
      };
    }
  }

  // If no error found anywhere
  if (!rawErr) {
    // Check if the event itself has code or failure message
    if (parsed.code || parsed.message || parsed.details) {
      rawErr = parsed;
    } else {
      return null;
    }
  }

  // Extract raw properties
  const rawCode = rawErr.code !== undefined && rawErr.code !== null && rawErr.code !== ''
    ? Number(rawErr.code)
    : null;

  const rawTitle = rawErr.title || '';
  const rawMessage = rawErr.message || '';
  const rawDetails = rawErr.error_data?.details || rawErr.details || '';

  // Look up known Meta error code
  const codeKey = rawCode !== null ? String(rawCode) : '';
  const meta = codeKey && ERROR_DB[codeKey] ? ERROR_DB[codeKey] : null;

  const categoryKey = meta?.category || (
    rawCode === 131026 || /not on whatsapp|undeliverable/i.test(rawTitle + ' ' + rawMessage)
      ? 'not_on_whatsapp'
      : rawCode === 131049 || /ecosystem|policy|restricted/i.test(rawTitle + ' ' + rawMessage)
        ? 'meta_restricted'
        : /rate limit/i.test(rawTitle + ' ' + rawMessage)
          ? 'rate_limit'
          : /template/i.test(rawTitle + ' ' + rawMessage)
            ? 'template_issue'
            : 'other'
  );

  const categoryMeta = CATEGORY_DB[categoryKey] || CATEGORY_DB['other'] || {
    key: 'other',
    label: 'Other Failures',
    icon: 'bi-exclamation-circle',
    color: '#6b7280',
    retriable: false
  };

  const shortName = meta?.short_name || rawTitle || (rawCode ? `Error ${rawCode}` : 'Delivery Failed');
  const title = meta?.title || rawTitle || shortName;
  const message = rawMessage || meta?.description || rawTitle || 'Message delivery failed.';
  const details = rawDetails || meta?.description || message;
  const action = meta?.action || (meta?.retriable ? 'This failure is retriable. You can retry after a short delay.' : 'Cannot be retargeted via WhatsApp with this configuration.');
  const retriable = meta ? meta.retriable : categoryMeta.retriable;
  const severity = meta?.severity || (retriable ? 'warning' : 'danger');
  const icon = meta?.icon || categoryMeta.icon || 'bi-exclamation-triangle';

  const badgeText = rawCode ? `${shortName} (${rawCode})` : shortName;

  return {
    code: rawCode,
    title,
    shortName,
    message,
    details,
    action,
    category: categoryKey,
    categoryLabel: categoryMeta.label,
    categoryIcon: categoryMeta.icon,
    retriable,
    icon,
    severity,
    badgeText,
    rawError: rawErr
  };
}
